export enum SubscriptionType {
  PassePartout = 0,
  PunchCard = 1,
  RecurringSubscription = 2,
}

export enum SubscriptionInstanceStatus {
  Active = 0,
  Paused = 1,
  Blocked = 2,
  Cancelled = 3,
  Expired = 4,
  PendingCard = 5,
  PendingMandate = 6, // Paid, but the SEPA mandate is not yet created; activates once the mandate is in place.
}

export enum SubscriptionInstanceBlockedReason {
  None = 0,
  NonPayment = 1,
  NoShows = 2,
  Admin = 3,
}

export enum SubscriptionBillingPeriodType {
  Yearly = 0,
  HalfYearly = 1,
  Quarterly = 2,
  Monthly = 3,
}

export enum BillingRenewalCycle {
  Periodic = 0,
  Anniversary = 1,
}

export enum CardFormat {
  Digits = 0,
  AlphaNumeric = 1,
  AlphaNumericSpecial = 2,
}

export enum CardGenerationMode {
  Auto = 0,
  PreGenerated = 1,
  Both = 2,
  /** Web sales generate a code like Both; the cash register links whatever card was scanned (PAUL-3676). */
  AcceptAny = 3,
}

export enum EarliestStartDate {
  Immediately = 0,
  NextDay = 1,
  DefinedDaysInFuture = 2,
  StartOfNextBillingPeriod = 3,
}

export enum MinContractDurationPeriod {
  EqualToBillingPeriod = 0,
  Years = 1,
  Months = 2,
}

export enum SubscriptionCancellationPeriod {
  Immediately = 0,
  EqualToBillingPeriod = 1,
  Years = 2,
  Months = 3,
}

export enum SubscriptionValidityPeriod {
  Years = 0,
  Months = 1,
  Days = 2,
  FixedDates = 3,
}

export enum UserDetailRequirement {
  NotRequired = 0,
  Optional = 1,
  Required = 2,
  RequiredDontDisplay = 3,
}

/**
 * Whether the buyer has to choose the subscription's start date while buying. Shaped after
 * {@link UserDetailRequirement} because the decision is the same one: hide the input, offer it, or
 * refuse the order without it. Owned by the Rules step rather than Contract terms — Contract terms
 * is recurring-only, while the start date also dates a punchcard's or passepartout's validity period.
 */
export enum SubscriptionStartDateRequirement {
  /** No picker; the subscription starts on the day of purchase. */
  NotRequired = 0,
  /** Picker shown; left empty it starts on the day of purchase. */
  Optional = 1,
  /** The order is refused until the buyer has chosen a date. */
  Required = 2,
}

export enum CardBatchType {
  Generated = 0,
  Uploaded = 1,
}

export enum InvoicePaymentStatus {
  Pending = 0,
  Paid = 1,
  Overdue = 2,
  Revoked = 3,
  // 4 and 5 are retired: the backend no longer sends them. Credit notes now arrive as Settled with
  // `is_credit_note` set, and what used to be Cancelled arrives as Voided. Kept so an old payload
  // from a stale cache still resolves to a label.
  CreditNote = 4,
  Cancelled = 5,
  // Resolved with no money moved. Says nothing about the invoice being credited - that row keeps its
  // own status, normally Paid, so Settled rows do not pair up. Only credit notes carry it in
  // practice, but test `is_credit_note` rather than this value.
  Settled = 6,
  // An unpaid future invoice on an ended subscription: never issued, never numbered, nothing booked.
  Voided = 7,
  // A credit note whose original invoice was already paid: that money is in Pauly's account and has
  // to go back to the holder. Pauly has no automatic refund path, so the payout is made by hand in
  // the Pay.nl dashboard and recorded through PUT /subscription-invoices/{id}/paid-out. A note can
  // reach this by itself - a SEPA debit submitted before the cancellation lands days later - and
  // leave it by itself when the bank reverses that debit.
  PayoutPending = 8,
  // That payout has been made and recorded. Only credit notes ever carry 8 or 9.
  PaidOut = 9,
}

/**
 * What the SEPA direct-debit engine is doing about one invoice (PAUL-3432). Deliberately not
 * derivable from InvoicePaymentStatus, which reads Pending both while a debit is in transit and
 * after one has been reversed - the ambiguity support could not resolve. The two fields are
 * allowed to disagree; never infer one from the other.
 */
export enum SubscriptionInvoiceCollectionState {
  /** No automatic collection will ever happen: not a SEPA-billed recurring product, or no mandate. */
  NotCollectable = 0,
  /** A route exists but this invoice is not on it: free period, paid in the portal, route closed. */
  NotScheduled = 1,
  /** Pre-notified, awaiting its collection_date. Nothing sent to the bank yet. */
  Scheduled = 2,
  /** Sent to the bank, awaiting the outcome. The money is on its way. */
  Submitted = 3,
  Paid = 4,
  /** Bank refused it. A collection_date means the one retry is still coming; null means it gave up. */
  Declined = 5,
  /** Storno: the holder reversed the debit after it was taken. */
  Reversed = 6,
}

/**
 * Where a credit note's refund through Pay. stands (PAUL-3730). Starts at 1: null on the wire
 * means the note was never paid out through Pay. - nothing sent yet, or a payout made by hand and
 * recorded with PUT /subscription-invoices/{id}/paid-out. Only credit notes ever carry it.
 */
export enum SubscriptionPayoutState {
  /** Pauly asked Pay. to refund the note; nothing has left yet. */
  Requested = 1,
  /** The refund is in Pay.'s bank batch. */
  Sent = 2,
  /** Pay. paid it out: the note is PaidOut (9), its reference Pay.'s refund id. */
  Completed = 3,
  /** Pay. could not pay it out (a closed account, say). The note is back in the payout queue. */
  Bounced = 4,
}

// Pauly-derived SEPA mandate status (Pay.nl exposes no mandate-level status).
export enum SubscriptionMandateStatus {
  Pending = 0,
  Active = 1,
  Cancelled = 2,
  Failed = 3,
}

/**
 * How the direct debit is settled when a subscription moves to another account (PAUL-3649). Starts
 * at 1 on the wire. Which ones apply is the preview's call, never derived here.
 */
export enum SubscriptionBuyerTransferMandateResolution {
  /** Nothing moves: collection continues under the new account's own direct debit. */
  UseTargetMandate = 1,
  /** The new account's direct debit is cancelled and the current one moves onto it. */
  ReplaceTargetMandate = 2,
  /** The current account's direct debit moves to the new account, which has none. */
  MoveSourceMandate = 3,
  /** No direct debit moves: the new account is e-mailed the invitation to set one up. */
  InviteTarget = 4,
}

// Calculation kind snapshotted on DiscountOnSubscription (type 1) discounts.
export enum SubscriptionDiscountKind {
  Percentage = 0,
  Amount = 1,
  FixedPrice = 2,
}

// MandateSetup = the €0.01 verification payment used by change-payment-method.
export enum SubscriptionInvoicePaymentPurpose {
  InvoicePayment = 0,
  Collection = 1,
  MandateSetup = 2,
}

export enum SubscriptionCardStatus {
  Active = 0,
  Blocked = 1,
}

export enum SubscriptionReservationStatus {
  Reserved = 0,
  Visited = 1,
  NoShow = 2,
  Cancelled = 3,
  Refunded = 4,
}

export const subscriptionInstanceStatusOptions = [
  { text: 'subscription_instances.status.0', val: SubscriptionInstanceStatus.Active },
  { text: 'subscription_instances.status.1', val: SubscriptionInstanceStatus.Paused },
  { text: 'subscription_instances.status.2', val: SubscriptionInstanceStatus.Blocked },
  { text: 'subscription_instances.status.3', val: SubscriptionInstanceStatus.Cancelled },
  { text: 'subscription_instances.status.4', val: SubscriptionInstanceStatus.Expired },
  { text: 'subscription_instances.status.5', val: SubscriptionInstanceStatus.PendingCard },
];

export const invoicePaymentStatusOptions = [
  { text: 'subscription_instances.payment_status.0', val: InvoicePaymentStatus.Pending },
  { text: 'subscription_instances.payment_status.1', val: InvoicePaymentStatus.Paid },
  { text: 'subscription_instances.payment_status.2', val: InvoicePaymentStatus.Overdue },
  { text: 'subscription_instances.payment_status.3', val: InvoicePaymentStatus.Revoked },
  { text: 'subscription_instances.payment_status.6', val: InvoicePaymentStatus.Settled },
  { text: 'subscription_instances.payment_status.7', val: InvoicePaymentStatus.Voided },
  { text: 'subscription_instances.payment_status.8', val: InvoicePaymentStatus.PayoutPending },
  { text: 'subscription_instances.payment_status.9', val: InvoicePaymentStatus.PaidOut },
];

/**
 * The `payment_status` filter of GET /v1.0/subscription-invoices takes one InvoicePaymentStatus
 * NAME, not its number: a number binds to any value of the underlying type and a comma-separated
 * list is ORed even on a non-Flags enum, so the backend refuses both. Typed as a total Record so a
 * new enum member cannot be added without naming it here.
 */
export const invoicePaymentStatusFilterName: Record<InvoicePaymentStatus, string> = {
  [InvoicePaymentStatus.Pending]: 'pending',
  [InvoicePaymentStatus.Paid]: 'paid',
  [InvoicePaymentStatus.Overdue]: 'overdue',
  [InvoicePaymentStatus.Revoked]: 'revoked',
  [InvoicePaymentStatus.CreditNote]: 'credit_note',
  [InvoicePaymentStatus.Cancelled]: 'cancelled',
  [InvoicePaymentStatus.Settled]: 'settled',
  [InvoicePaymentStatus.Voided]: 'voided',
  [InvoicePaymentStatus.PayoutPending]: 'payout_pending',
  [InvoicePaymentStatus.PaidOut]: 'paid_out',
};

export const subscriptionMandateStatusOptions = [
  { text: 'subscription_instances.mandate_status.0', val: SubscriptionMandateStatus.Pending },
  { text: 'subscription_instances.mandate_status.1', val: SubscriptionMandateStatus.Active },
  { text: 'subscription_instances.mandate_status.2', val: SubscriptionMandateStatus.Cancelled },
  { text: 'subscription_instances.mandate_status.3', val: SubscriptionMandateStatus.Failed },
];

export const subscriptionBillingPeriodTypeOptions = [
  { text: 'subscription_instances.billing_period.0', val: SubscriptionBillingPeriodType.Yearly },
  { text: 'subscription_instances.billing_period.1', val: SubscriptionBillingPeriodType.HalfYearly },
  { text: 'subscription_instances.billing_period.2', val: SubscriptionBillingPeriodType.Quarterly },
  { text: 'subscription_instances.billing_period.3', val: SubscriptionBillingPeriodType.Monthly },
];

// ── Portal subscriptions v2 (GET /v2.0/portal/subscriptions) ──────────────────

// Whether the next charge is an invoice that already exists or one derived from
// the schedule. The engine raises an invoice ten days before its period starts
// (the SEPA pre-notification), and from that moment the debit cannot move.
export enum SubscriptionChargeSource {
  Issued = 0,
  Forecast = 1,
}

export enum SubscriptionPeriodChangeReason {
  HolderRequested = 0,
  // The product's automatic switch at the end of the minimum commitment.
  PostCommitmentSwitch = 1,
}

// What the holder has to do about a subscription, decided server-side — these
// replace the predicates the portal used to derive from five separate reads.
export enum SubscriptionAttentionKind {
  MissingUserDetails = 0,
  MandateSetupRequired = 1,
  MandateReplacementRequired = 2,
  // Carries `invoice_ids` (oldest first) and the total `amount`.
  OutstandingInvoice = 3,
  // Carries `blocked_reason`.
  Blocked = 4,
}

export enum SubscriptionAttentionSeverity {
  Info = 0,
  Warning = 1,
}
